import { create } from 'zustand';
import { logger } from '../utils/logger';

/**
 * UI state for notes that's separate from the data fetching logic
 * This store only manages UI-specific state like filters, sorting, and UI flags
 */
interface NotesUIState {
  // Active filters
  activeFilters: {
    searchQuery: string;
    userIds: string[];
  };
  
  // UI state flags
  isNotesPanelOpen: boolean;
  selectedNoteId: string | null;
  editingNoteId: string | null;
  confirmDeleteNoteId: string | null;
  
  // UI state actions
  setSearchQuery: (query: string) => void;
  toggleUserFilter: (userId: string) => void;
  setUserFilters: (userIds: string[]) => void;
  resetFilters: () => void;
  
  // Panel and note selection actions
  openNotePanel: (noteId: string) => void;
  closeNotePanel: () => void;
  setEditingNote: (noteId: string | null) => void;
  setConfirmDeleteNote: (noteId: string | null) => void;
}

export const useNotesUIStore = create<NotesUIState>((set) => ({
  // Default filter state
  activeFilters: {
    searchQuery: '',
    userIds: [],
  },
  
  // Default UI state
  isNotesPanelOpen: false,
  selectedNoteId: null,
  editingNoteId: null,
  confirmDeleteNoteId: null,
  
  // Filter actions
  setSearchQuery: (query) => {
    set((state) => ({
      activeFilters: {
        ...state.activeFilters,
        searchQuery: query,
      },
    }));
    
    logger.component('Updated note search query', { query });
  },
  
  toggleUserFilter: (userId) => {
    set((state) => {
      const userIds = [...state.activeFilters.userIds];
      const index = userIds.indexOf(userId);
      
      if (index === -1) {
        userIds.push(userId);
      } else {
        userIds.splice(index, 1);
      }
      
      return {
        activeFilters: {
          ...state.activeFilters,
          userIds,
        },
      };
    });
  },
  
  setUserFilters: (userIds) => {
    set((state) => ({
      activeFilters: {
        ...state.activeFilters,
        userIds,
      },
    }));
    
    logger.component('Updated note user filters', { userCount: userIds.length });
  },
  
  resetFilters: () => {
    set({
      activeFilters: {
        searchQuery: '',
        userIds: [],
      },
    });
    
    logger.component('Reset all note filters');
  },
  
  // Panel and note selection actions
  openNotePanel: (noteId) => {
    set({
      selectedNoteId: noteId,
      isNotesPanelOpen: true,
      editingNoteId: null,
      confirmDeleteNoteId: null,
    });
  },
  
  closeNotePanel: () => {
    set({
      isNotesPanelOpen: false,
      editingNoteId: null,
      confirmDeleteNoteId: null,
      // Keep selectedNoteId for animation purposes,
      // it will be cleared after animation completes
    });
    
    // Clear the selected note ID after animation completes
    setTimeout(() => {
      set({ selectedNoteId: null });
    }, 300);
  },
  
  setEditingNote: (noteId) => {
    set({ 
      editingNoteId: noteId,
      confirmDeleteNoteId: null,
    });
  },
  
  setConfirmDeleteNote: (noteId) => {
    set({ 
      confirmDeleteNoteId: noteId,
      editingNoteId: null,
    });
  },
}));